import { LEGAL } from "./legal";

/**
 * The help page's questions, as plain text: the page renders them and the FAQPage
 * structured data repeats them word for word, which is what search engines require of it.
 * Each `id` is a stable anchor (`/aide#annuler`) that other screens link to.
 */
export type HelpQuestion = { id: string; question: string; answer: string[] };
export type HelpSection = { id: string; title: string; intro: string; questions: HelpQuestion[] };

export const HELP_SECTIONS: HelpSection[] = [
  {
    id: "offrir",
    title: "J'offre un cadeau",
    intro: "On vous a envoyé une liste ? Voici tout ce qu'il faut savoir, en deux minutes.",
    questions: [
      {
        id: "compte",
        question: "Faut-il créer un compte pour réserver un cadeau ?",
        answer: [
          "Non. Ouvrez le lien de la liste, choisissez un cadeau et cliquez sur « Je l'offre ». Votre prénom et votre email sont facultatifs.",
        ],
      },
      {
        id: "reserver",
        question: "Comment réserver un cadeau, et la personne le saura-t-elle ?",
        answer: [
          "Cliquez sur « Je l'offre » sous le cadeau. Il apparaît aussitôt comme pris pour les autres invités : personne n'achètera le même.",
          "La personne qui reçoit ne voit pas ce qui est réservé : c'est le mode surprise, activé par défaut. Elle découvre qui a offert quoi après la fête.",
        ],
      },
      {
        id: "acheter",
        question: "Où acheter le cadeau, et comment le faire livrer ?",
        answer: [
          "Où vous voulez. Le bouton « Voir sur… » mène à la boutique choisie par l'auteur de la liste, mais vous pouvez l'acheter ailleurs, en ligne ou en magasin.",
          "Si l'auteur a indiqué une adresse de livraison, elle s'affiche juste après votre réservation, et dans le mail de confirmation si vous avez laissé votre email. Sinon, faites-le livrer chez vous et offrez-le en main propre.",
          "Listou ne vend rien et ne prend aucun paiement : vous payez directement la boutique.",
        ],
      },
      {
        id: "prix",
        question: "Le prix affiché est-il le bon ?",
        answer: [
          "C'est un prix indicatif, relevé quand le cadeau a été ajouté. Seul le prix affiché par la boutique au moment de l'achat fait foi.",
        ],
      },
      {
        id: "plusieurs",
        question: "Peut-on offrir un cadeau à plusieurs ?",
        answer: [
          "Quand l'auteur en souhaite plusieurs (deux assiettes, trois bodies), chacun peut en réserver une partie : la liste indique combien il en reste.",
          "Pour un gros cadeau, mettez-vous d'accord entre vous, puis réservez-le une fois au nom du groupe, par exemple « Les cousins ».",
        ],
      },
      {
        id: "cagnotte",
        question: "Peut-on participer sans offrir un cadeau précis ?",
        answer: [
          "Oui, si l'auteur a ajouté une cagnotte : le bloc « Participer librement », en bas de la liste, y mène. Vous pouvez aussi, tout simplement, glisser une enveloppe le jour de la fête.",
        ],
      },
      {
        id: "anonyme",
        question: "Peut-on offrir sans que la personne sache que c'est nous ?",
        answer: [
          "Oui : cochez « Rester anonyme » en réservant. La personne qui reçoit ne verra jamais votre prénom, même après la fête. Les autres invités ne le voient dans aucun cas.",
        ],
      },
      {
        id: "annuler",
        question: "Comment annuler une réservation ?",
        answer: [
          "Rouvrez la liste depuis le même téléphone ou ordinateur : sous votre cadeau, cliquez sur « Annuler ma réservation ». Si vous avez laissé votre email, le mail de confirmation contient aussi un lien d'annulation.",
          `Vous avez changé d'appareil et n'avez pas laissé d'email ? Écrivez-nous à ${LEGAL.contactEmail} avec le lien de la liste et le cadeau concerné.`,
        ],
      },
    ],
  },
  {
    id: "creer",
    title: "Je crée une liste",
    intro:
      "Pour une naissance, un mariage, Noël ou un anniversaire : gratuit, et prêt en quelques minutes.",
    questions: [
      {
        id: "ajouter",
        question: "Comment ajouter des envies ?",
        answer: [
          "Collez le lien d'un produit, de n'importe quelle boutique : Listou récupère le nom, la photo et le prix. Vous pouvez aussi chercher parmi les produits que d'autres ont déjà ajoutés, ou écrire une envie à la main.",
          "En panne d'idées ? Sous « Ajouter une envie », l'assistant vous en propose selon l'occasion.",
        ],
      },
      {
        id: "priorites",
        question: "À quoi servent « Coup de cœur », « Envie » et « Idée » ?",
        answer: [
          "Ce sont trois niveaux d'envie. Les coups de cœur passent en tête de la liste avec un cœur rouge ; une « Idée » dit à vos proches que ce serait bien, sans être indispensable.",
        ],
      },
      {
        id: "surprise",
        question: "Le mode surprise, qu'est-ce que je vois et ne vois pas ?",
        answer: [
          "Tant qu'il est actif, ni vous ni vos co-organisateurs ne voyez ce qui est réservé, ni par qui. Si vous ouvrez le lien de votre propre liste, Listou vous propose l'aperçu : la page telle que la voient vos proches, sans les réservations.",
          "Pour une liste d'enfant que vous gérez vous-même, désactivez-le dans l'onglet Réservations : vous verrez ce qui est pris et éviterez d'acheter un doublon.",
        ],
      },
      {
        id: "revelation",
        question: "Quand vais-je découvrir qui a offert quoi ?",
        answer: [
          "Le lendemain de la date de l'événement, ou dès que vous archivez la liste. Vous pouvez aussi cliquer sur « Révéler maintenant » dans l'onglet Réservations, et « Cacher à nouveau » si c'était trop tôt.",
          "Les invités qui ont choisi de rester anonymes apparaissent comme « un proche ».",
        ],
      },
      {
        id: "co-organisateurs",
        question: "Peut-on gérer une liste à deux ?",
        answer: [
          "Oui, dans l'onglet Réglages. Un co-propriétaire, comme votre conjoint ou l'autre parent, a les mêmes droits que vous. Un invité « Peut modifier » aide à remplir la liste, sans toucher au partage ni à la surprise.",
        ],
      },
      {
        id: "adresse",
        question: "Qui voit mon adresse de livraison ?",
        answer: [
          "Elle n'est jamais affichée sur la liste. Un proche la voit seulement après avoir réservé, à l'écran et dans son mail de confirmation.",
          "Réserver ne demande pas de compte : toute personne qui a le lien peut donc la voir. Ne l'ajoutez que si vous partagez la liste avec vos proches.",
        ],
      },
      {
        id: "partager",
        question: "Comment partager ma liste, ou arrêter de la partager ?",
        answer: [
          "L'onglet Partage donne le lien, un QR code et des visuels pour Instagram. Seules les personnes qui ont le lien voient la liste : elle n'apparaît pas dans les moteurs de recherche.",
          "Le lien est allé trop loin ? Dans Réglages, « Changer le lien de partage » désactive l'ancien. Vous pouvez aussi rendre la liste privée le temps de la préparer.",
        ],
      },
      {
        id: "avis",
        question: "Pourquoi Listou me demande-t-il mon avis sur mes cadeaux ?",
        answer: [
          "Une semaine après l'événement, nous vous demandons une note sur les cadeaux reçus, directement dans l'onglet Réservations. Votre avis s'affiche, avec votre prénom, sur la page du produit, et aide d'autres familles à choisir.",
        ],
      },
      {
        id: "gratuit",
        question: "Listou est-il gratuit ? Comment gagnez-vous de l'argent ?",
        answer: [
          "Créer une liste et réserver un cadeau sont gratuits. Certains liens vers les boutiques sont affiliés : si un proche achète après avoir cliqué, la boutique peut nous verser une commission, sans aucun surcoût pour lui.",
        ],
      },
    ],
  },
  {
    id: "groupes",
    title: "Groupes et Secret Santa",
    intro: "Plusieurs listes derrière un même lien, avec ou sans tirage au sort.",
    questions: [
      {
        id: "qui-etes-vous",
        question: "On m'a envoyé le lien d'un groupe : que faire ?",
        answer: [
          "Choisissez votre prénom. Vous arrivez sur votre liste, que vous remplissez comme vous voulez, et vous voyez celles des autres, sur lesquelles vous pouvez réserver.",
          "Votre prénom n'est pas dans la liste ? Cliquez sur « Je ne suis pas dans la liste ». Vous voulez seulement jeter un œil ? « Juste regarder ».",
        ],
      },
      {
        id: "autre-appareil",
        question: "J'ai choisi mon prénom sur un autre téléphone, comment le retrouver ?",
        answer: [
          "Rechoisissez-le simplement. Si vous aviez ajouté votre email, on vous envoie un code pour confirmer que c'est bien vous.",
          "Pour un Secret Santa déjà tiré, votre lien personnel vous est renvoyé par email, ou l'organisateur peut vous le copier.",
        ],
      },
      {
        id: "pas-moi",
        question: "J'ai cliqué sur le mauvais prénom",
        answer: [
          "Cliquez sur « Ce n'est pas moi ». La place redevient libre et sa liste revient à l'organisateur.",
          "Dans un Secret Santa, si vous aviez déjà découvert le tirage, il est échangé discrètement avec celui de quelqu'un qui n'a pas encore regardé : ce que vous avez vu n'est plus vrai.",
        ],
      },
      {
        id: "gommettes",
        question: "À quoi servent les gommettes du Secret Santa ?",
        answer: [
          "Deux personnes avec la même gommette ne se tirent pas entre elles : un couple, une famille, des collègues d'un même bureau. L'organisateur peut aussi empêcher deux gommettes de se tirer, ou exclure une personne précise.",
        ],
      },
      {
        id: "desabonner",
        question: "Je reçois des emails de Secret Santa, comment les arrêter ?",
        answer: [
          "Cliquez sur « Ne plus recevoir ces messages » en bas de l'email : c'est immédiat, sans compte. Les emails que vous demandez vous-même, comme un code de connexion, continuent d'arriver.",
        ],
      },
    ],
  },
];

/** schema.org FAQPage: every question, its answer as the page shows it. */
export const helpJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: HELP_SECTIONS.flatMap((section) =>
    section.questions.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer.join("\n\n") },
    })),
  ),
});
